import { useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Button, Flex, Form, Input, List, Typography } from "antd"
import { useForm } from "antd/es/form/Form"
import FormItem from "antd/es/form/FormItem"
import { ArrowLeftOutlined } from "@ant-design/icons"
// @ts-ignore
import * as QRious from "qrious"

import appleStore from "@src/app/assets/icons/apple-store.svg"
import arrowImage from "@src/app/assets/icons/arrow-right.svg"
import cameraImage from "@src/app/assets/icons/camera-photo.svg"
import closedEye from "@src/app/assets/icons/closedEye.svg"
import googleAuthenticator from "@src/app/assets/icons/google-authenticator.png"
import googlePlay from "@src/app/assets/icons/google-play.svg"
import openEye from "@src/app/assets/icons/openEye.svg"
import plusImage from "@src/app/assets/icons/plus.svg"
import { authStore } from "@src/app/authStore"
import { FormContainer } from "@src/app/components/FormContainer"
import { securityApi } from "@src/shared/api"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import { useResourceLoader } from "@src/shared/resource/resource"
import css from "./index.module.css"

const { Item } = List
export const SettingsOTP = observer(({ onClose }: { onClose?: any }) => {
  const { isOtpEnabled, merchantInfo } = authStore
  const { Title } = Typography
  return (
    <div>
      <div className={css.navigation}>
        <Button
          icon={<ArrowLeftOutlined />}
          id="GENERATED_ID_SETTINGSOTP_BUTTON_BACK_1"
          onClick={onClose}
          type="default"
        >
          Back
        </Button>
        <Title
          className={css.title}
          level={4}
          style={{
            color: "#303030",
            display: "flex",
            justifyContent: "center",
            marginBottom: 0,
            marginLeft: "10px",
            marginRight: "89px",
            marginTop: 0,
            width: "404px",
          }}
        >
          2FA11
        </Title>
      </div>
      <div className={css.page}>
        {isOtpEnabled ? <DisableOTPPage /> : <EnableOTPPage />}
      </div>
    </div>
  )
})

const DisableOTPPage = observer(() => {
  const [form] = useForm()
  const [isPending, setIsPending] = useState<boolean>(false)
  const { submit } = useCatchFormErrors(form, (values) => {
    setIsPending(true)
    return securityApi
      .otpUnset(values)
      .then(() => window.location.reload())
      .finally(() => setIsPending(false))
  })

  return (
    <div className={css.step}>
      <Form
        form={form}
        id="GENERATED_ID_SETTINGSOTP_FORM_VOID_1"
        onFinish={submit}
      >
        <Flex gap={16} vertical>
          <FormItem
            id="GENERATED_ID_SETTINGSOTP_FORMITEM_VOID_1"
            label="Code"
            name="code"
            required
          >
            <Input autoComplete="off" size="large" />
          </FormItem>
          <FormItem
            id="GENERATED_ID_SETTINGSOTP_FORMITEM_VOID_2"
            label="Password"
            name="password"
            required
          >
            <Input.Password autoComplete="off" size="large" />
          </FormItem>
          <FormItem id="GENERATED_ID_SETTINGSOTP_FORMITEM_VOID_3">
            <Button
              block
              className={css.button}
              htmlType="submit"
              id="GENERATED_ID_SETTINGSOTP_BUTTON_DISABLE_1"
              loading={isPending}
              size="large"
              type="primary"
            >
              Disable
            </Button>
          </FormItem>
        </Flex>
      </Form>
    </div>
  )
})

type ResponseData = {
  secret: string
  uri: string
}
const EnableOTPPage = () => {
  const { data, isPending } = useResourceLoader<ResponseData>(() =>
    // @ts-ignore
    securityApi.otpGenerate().then((r) => r.data.data as ResponseData),
  )

  const [isPendingOTP, setIsPendingOTP] = useState<boolean>(false)
  const [form] = useForm()
  const [showPassword, setShowPassword] = useState(false)
  const handleTogglePassword = () => {
    setShowPassword(!showPassword)
  }

  const { submit } = useCatchFormErrors(form, (values) => {
    setIsPendingOTP(true)
    return securityApi
      .otpSet({ ...values, secret: data?.secret })
      .then(() => window.location.reload())
      .finally(() => setIsPendingOTP(false))
  })

  const qrCode = useMemo(() => {
    if (!data) return null

    return new QRious({
      background: "transparent",
      backgroundAlpha: 0.8,
      foreground: "black",
      foregroundAlpha: 0.8,
      padding: 0,
      size: 213,
      value: data!.uri,
    }).toDataURL()
  }, [data?.uri])

  return (
    <List>
      <div style={{ display: "flex", gap: "24px" }}>
        <Item style={{ border: "none", padding: 0, width: "100%" }}>
          <div className={css.step1}>
            <div className={css.stepTitle}>
              1. Install Google Authentication
            </div>
            <div className={css.googleAuthenticator}>
              <img className={css["ga-icon"]} src={googleAuthenticator} />
              <div>
                <div className={css.gaName}>Google Authentication</div>
                <div className={css.gaAvailableOn}>
                  <a
                    className={css.gaStore}
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  >
                    <img alt="Google Play" src={googlePlay} />
                  </a>
                  <a
                    className={css.gaStore}
                    href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                  >
                    <img alt="Apps Store" src={appleStore} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Item>
        <Item style={{ border: "none", padding: 0, width: "100%" }}>
          <div className={css.step2}>
            <div className={css.stepTitle}>
              2. Open Google Authentication app
            </div>
            <div className={css.borderStep}>
              <div className={css.stepHelp}>Tap "+" and hit "Scan QR Code"</div>
              <div className={css.step2Image}>
                <img alt="plus image" src={plusImage} />
                <img alt="arrow image" src={arrowImage} />
                <img alt="camera image" src={cameraImage} />
              </div>
            </div>
          </div>
        </Item>
      </div>
      <Item style={{ border: "none", padding: 0, width: "100%" }}>
        <div className={css.step3}>
          <div className={css.stepTitle}>
            3. Scan QR code while in the Google Authentication app
          </div>
          <div className={css.step3QRCode}>
            <img className={css.qrCode} src={qrCode} />
          </div>
        </div>
      </Item>
      <Item
        className={css.stepForm}
        style={{ border: "none", display: "block", padding: 0 }}
      >
        <div className={css.stepTitle}>
          4. Enter the 6-digest from Authenticator
        </div>
        <div className={css.step4}>
          <FormContainer
            autoComplete="false"
            className={css.form}
            form={form}
            name="disableOtp"
            onFinish={submit}
          >
            <Flex gap={16} vertical>
              <Form.Item
                id="GENERATED_ID_SETTINGSOTP_FORM_ITEM_VOID_1"
                label="Code"
                name="code"
                required
              >
                <Input />
              </Form.Item>
              <Form.Item
                id="GENERATED_ID_SETTINGSOTP_FORM_ITEM_VOID_2"
                label="Current Password"
                name="password"
                required
              >
                <Input.Password />
              </Form.Item>
              <Button
                className={css.btn2fa}
                disabled={isPending || isPendingOTP}
                htmlType="submit"
                id="GENERATED_ID_SETTINGSOTP_BUTTON_ENABLE_2FA_1"
                loading={isPending || isPendingOTP}
                size="large"
                type="primary"
              >
                Enable 2FA
              </Button>
            </Flex>
          </FormContainer>
        </div>
      </Item>
    </List>
  )
}
